import {
  Component,
  input,
  output,
  contentChild,
  TemplateRef,
  ChangeDetectionStrategy,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import type { EmblaOptionsType } from 'embla-carousel';
import { ZardCarouselModule } from '../zard/carousel/carousel.module';

/**
 * Carousel Wrapper Component
 * 
 * A simplified wrapper around the Zard Carousel components for easier reuse.
 * 
 * @example
 * ```html
 * <app-carousel [items]="myItems" [options]="{ align: 'start', loop: true }">
 *   <ng-template #itemTemplate let-item>
 *     <div>{{ item.name }}</div>
 *   </ng-template>
 * </app-carousel>
 * ```
 * 
 * @example Responsive breakpoints
 * ```html
 * <app-carousel 
 *   [items]="plans" 
 *   [breakpoints]="{ mobile: 'full', tablet: '1/2', desktop: '1/3' }">
 *   <ng-template #itemTemplate let-item>
 *     <div>{{ item }}</div>
 *   </ng-template>
 * </app-carousel>
 * ```
 */
@Component({
  selector: 'app-carousel',
  standalone: true,
  imports: [CommonModule, ZardCarouselModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  template: `
    <div [class]="wrapperClass()">
      <z-carousel [zOptions]="options()" [zPlugins]="plugins()" [zEnableWheelScroll]="enableWheelScroll()" (onSlideChange)="slideChange.emit($event)">
        <z-carousel-content [class]="contentSpacingClass()">
          @for (item of items(); track trackBy() ? trackBy()!(index, item) : index; let index = $index) {
            <z-carousel-item [class]="getItemClass()">
              <ng-container *ngTemplateOutlet="itemTemplate()!; context: { $implicit: item, index: index }" />
            </z-carousel-item>
          }
        </z-carousel-content>
      </z-carousel>
    </div>
  `,
})
export class CarouselComponent<T = any> {
  /**
   * Array of items to display in the carousel
   */
  readonly items = input.required<T[]>();

  /**
   * Template reference for rendering each item
   */
  readonly itemTemplate = contentChild<TemplateRef<any>>('itemTemplate');

  /**
   * Embla carousel options
   * @default { align: 'start', loop: false }
   */
  readonly options = input<EmblaOptionsType>({ align: 'start', loop: false });

  /**
   * Embla carousel plugins (e.g., Autoplay)
   */
  readonly plugins = input<any[]>([]);

  /**
   * Enable wheel/scroll navigation
   * @default true
   */
  readonly enableWheelScroll = input<boolean>(true);

  /**
   * Responsive breakpoints configuration
   * @example { mobile: 'full', tablet: '1/2', desktop: '1/3' }
   */
  readonly breakpoints = input<{
    mobile?: string;
    tablet?: string;
    desktop?: string;
    largeDesktop?: string;
    extraLargeDesktop?: string;
  }>({
    mobile: 'full',
    tablet: '1/2',
    desktop: '1/2',
    largeDesktop: '1/3',
    extraLargeDesktop: '1/4'
  });

  /**
   * Spacing between items
   * @default '4'
   */
  readonly spacing = input<'2' | '4' | '6' | '8'>('4');

  /**
   * Additional CSS classes for the wrapper
   */
  readonly wrapperClass = input<string>('w-full px-4 md:px-12');

  /**
   * Track by function for better performance
   */
  readonly trackBy = input<(index: number, item: T) => any>();

  /**
   * Event emitted when slide changes
   */
  readonly slideChange = output<number>();

  /**
   * Get the content spacing class based on spacing input
   */
  protected contentSpacingClass(): string {
    const spacingMap = {
      '2': '-ml-2',
      '4': '-ml-4',
      '6': '-ml-6',
      '8': '-ml-8',
    };
    return spacingMap[this.spacing()];
  }

  /**
   * Get the item class combining responsive breakpoints and spacing
   */
  protected getItemClass(): string {
    const bp = this.breakpoints();
    const spacingMap = {
      '2': 'pl-2',
      '4': 'pl-4',
      '6': 'pl-6',
      '8': 'pl-8',
    };

    const baseClasses = [spacingMap[this.spacing()]];

    // Add responsive classes
    if (bp.mobile) {
      baseClasses.push(`basis-${bp.mobile}`);
    }
    if (bp.tablet) {
      baseClasses.push(`md:basis-${bp.tablet}`);
    }
    if (bp.desktop) {
      baseClasses.push(`lg:basis-${bp.desktop}`);
    }
    if (bp.largeDesktop) {
      baseClasses.push(`xl:basis-${bp.largeDesktop}`);
    }
    if (bp.extraLargeDesktop) {
      baseClasses.push(`2xl:basis-${bp.extraLargeDesktop}`);
    }

    return baseClasses.join(' ');
  }
}
